import type { QueryClient } from '@tanstack/react-query';
import type { UpdateFunnelConversationContext } from '../types/cache/funnel-conversations/optimistic.types';
import { getConversationRollbackSnapshot } from '../utils/cache/funnel-conversations/rollback-snapshot.util';
import { updateFunnelConversationCache } from '../utils/cache/funnel-conversations/update.util';
import { recoverCanonicalConversation } from './canonical-conversation-recovery.service';

/**
 * Restores only snapshots still owned by the failed operation. If another save
 * or accepted event changed this row, canonical recovery resolves its remaining
 * optimistic fields without rolling back any newer authority or unrelated rows.
 * @param client - Account-owned cache and mutation registry.
 * @param context - Original per-conversation snapshots and ownership check.
 */
export const rollbackConversationMutation = (
  client: QueryClient,
  context: UpdateFunnelConversationContext
): void => {
  const tenantId =
    context.previousConversation?.tenantId ??
    context.previousConversationDocument?.tenantId;
  if (!tenantId) return;
  const state = context.getCacheState?.(tenantId) ?? 'current';
  if (state === 'detached') return;
  if (state === 'changed') {
    const pending = client.isMutating({
      mutationKey: ['crm/funnel-conversations/update'],
      predicate: ({ state: mutation }) => {
        const variables: unknown = mutation.variables;
        return (
          typeof variables === 'object' &&
          variables !== null &&
          (('id' in variables && variables.id === context.params.id) ||
            ('conversationIds' in variables &&
              Array.isArray(variables.conversationIds) &&
              variables.conversationIds.includes(context.params.id)))
        );
      },
    });
    if (pending <= 1) {
      void recoverCanonicalConversation(client, context.params.id, tenantId);
    }
    return;
  }
  updateFunnelConversationCache(
    client,
    context,
    getConversationRollbackSnapshot(context.previousConversation),
    getConversationRollbackSnapshot(context.previousConversationDocument)
  );
};
